import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays, Clock, ImageIcon, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { EventPeopleStrip } from "@/components/events/EventPeople";
import { formatDate, formatTime } from "@/lib/format";

export default function EventCard({ ev, onViewFlyer, index = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1], delay: index * 0.05 }} className="h-full">
      <article className="group flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-card transition-shadow duration-300 hover:shadow-[0_24px_60px_rgba(92,52,18,0.12)]" data-testid="landing-event-card">
        {ev.flyer_image && (
          <button
            type="button"
            onClick={() => onViewFlyer?.(ev)}
            className="relative block w-full overflow-hidden border-b border-border bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label={`View ${ev.title} flyer`}
            data-testid="landing-event-flyer"
          >
            <AspectRatio ratio={4 / 5}>
              <img src={ev.flyer_image} alt={`${ev.title} flyer`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
            </AspectRatio>
            <span className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-end gap-1 bg-gradient-to-t from-black/60 to-transparent p-3 text-xs font-medium text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
              <ImageIcon className="h-3.5 w-3.5" /> View flyer
            </span>
          </button>
        )}
        <div className="flex flex-1 flex-col p-5">
          <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground">
              <CalendarDays className="h-3.5 w-3.5" /> {formatDate(ev.date)}
            </span>
            {ev.is_featured && <Badge className="border border-primary/40 bg-primary/15 text-primary hover:bg-primary/15">Next</Badge>}
          </div>
          <h3 className="mt-4 font-display text-xl font-semibold leading-snug text-foreground">{ev.title}</h3>
          {ev.theme_title && <p className="mt-1 text-sm font-medium text-primary">Theme: {ev.theme_title}</p>}
          <div className="mt-3 space-y-1.5 text-sm text-muted-foreground">
            {(ev.start_time || ev.end_time) && (
              <div className="flex items-center gap-2"><Clock className="h-4 w-4" />{formatTime(ev.start_time)}{ev.end_time ? ` – ${formatTime(ev.end_time)}` : ""}</div>
            )}
            {ev.location && (
              <div className="flex items-center gap-2"><MapPin className="h-4 w-4" />{ev.location.name}{ev.location.room ? ` · ${ev.location.room}` : ""}</div>
            )}
          </div>
          {ev.people?.length > 0 && <EventPeopleStrip people={ev.people} className="mt-4" />}
          <div className="mt-auto flex items-center justify-end pt-5">
            <Button asChild size="sm" variant="secondary" className="rounded-full">
              <Link to={`/events/${ev.event_id}`}>Details <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </article>
    </motion.div>
  );
}
